import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router-dom'
import axios from 'axios'

import Header from '../header/Header'
import Spinner from '../common/Spinner'
import LoadingSpinner from '../common/LoadingSpinner'
import ProductCard from '../catalogue/nodes/ProductCard'
import FolderProduct from '../catalogue/nodes/FolderProduct'
import { Breadcrumb, HeaderBreadcrumb, BreadcrumbPortal } from '../common/Breadcrumb'
import { BusinessUnitMultiSelect } from '../fields'
import ProductBrowseV2 from './ProductBrowseV2'
import FinalProductBrowseV2 from './FinalProductBrowseV2'
import { sinAcentos, segmentoNombre } from './catalogoTexto'
import { articleSearchApi } from '../../api/articleSearchApi'
import { routes } from '../../constantes/routes'
import '../../assests/styles/catalogue/CatalogueCards.scss'
import '../../assests/styles/catalogue/NodesV2.scss'
import '../../assests/styles/catalogue/catalogueButtons.scss'
import '../../assests/styles/catalogue/ProductCard.scss'

/**
 * Nivel 2 del Catálogo —el contenido de una categoría— con multicategoría.
 *
 * Sustituye a ProductsOfACategoryV2 (dentro de NodesV2.js), que se queda
 * intacto. La diferencia está en el origen de la pertenencia: aquí la tabla
 * N:M, allí la columna Articulos.id_TipoCategoria. Por eso un artículo puede
 * aparecer en varias categorías.
 *
 * Sólo se monta cuando el motor es v2 o comparar: CatalogueSearchHost devuelve
 * el catálogo antiguo antes de llegar a esta ruta cuando el motor es legacy.
 * No hace falta ninguna condición aquí dentro.
 *
 * Los enlaces hacia abajo usan las MISMAS rutas que el catálogo antiguo, así
 * que al entrar en una família se sigue navegando con él, sin costuras.
 */

const TAM_PAGINA = 60

const CategoryBrowseV2 = ({ match }) => {
  const idCategoria = match.params.id
  const history = useHistory()
  const base = `${routes.CATALOGUE_V2}/categoria/${idCategoria}`

  const categorias = useSelector((estado) => estado.categories)
  const categoria = (categorias || []).find(
    (c) => String(c.id_TipoCategoria) === String(idCategoria),
  )

  const [unidades, setUnidades] = useState([])
  const [texto, setTexto] = useState('')
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)

  const peticionRef = useRef(0)
  const unidadesTexto = unidades.join(',')

  useEffect(() => {
    const fuente = axios.CancelToken.source()
    const idPeticion = peticionRef.current + 1
    peticionRef.current = idPeticion

    setCargando(true)
    setError(null)

    articleSearchApi
      .category(
        idCategoria,
        { page: 1, pageSize: TAM_PAGINA, businessUnits: unidadesTexto },
        fuente.token,
      )
      .then((res) => {
        if (idPeticion !== peticionRef.current) return
        setItems(res.data.items || [])
        setTotal(res.data.total)
        setCargando(false)
      })
      .catch((err) => {
        if (idPeticion !== peticionRef.current) return
        if (!axios.isCancel(err)) {
          const codigo = err && err.response ? err.response.status : '—'
          setError(`No se ha podido cargar la categoría (HTTP ${codigo}).`)
        }
        setCargando(false)
      })

    return () => {
      peticionRef.current += 1
      fuente.cancel()
    }
  }, [idCategoria, unidadesTexto])

  // Filtrado local, igual que hace hoy esta pantalla: opera sobre lo ya
  // cargado y no vuelve al servidor.
  const visibles = texto.trim()
    ? items.filter((i) => sinAcentos(i.nombre).includes(sinAcentos(texto)))
    : items

  const carpetas = visibles.filter((i) => !i.isArticuloFinal)
  const finales = visibles.filter((i) => i.isArticuloFinal)

  const recuento = [
    `${total} elementos`,
    texto.trim() && visibles.length !== total ? `${visibles.length} filtrados` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      {/* Las dos migajas van FUERA del Switch, así que siguen puestas al
          bajar a una família o a una ficha: el rastro se acumula en vez de
          sustituirse. Y la raíz se pinta aquí porque, a diferencia del
          catálogo antiguo, este nivel no cuelga de la raíz sino que es una
          ruta hermana; sin esto el rastro empezaría por la categoría. */}
      <Breadcrumb to={routes.CATALOGUE_V2}>Catálogo - Búsqueda Global</Breadcrumb>
      <Breadcrumb to={base}>
        {categoria && categoria.nombre ? categoria.nombre : 'Categoría'}
      </Breadcrumb>

      <Switch>
        <Route
          path={`${match.url}/product/:idProduct/:nombre`}
          component={ProductBrowseV2}
        />
        <Route
          path={`${match.url}/final/:idProducto`}
          component={FinalProductBrowseV2}
        />

        <Route>{renderizarCategoria()}</Route>
      </Switch>
    </>
  )

  function renderizarCategoria() {
    return (
    <div className='products'>
      <Header text={'Catálogo'} route={() => history.goBack()} backBtn={true} />

      <div className='container-fluid m-0 p-0'>
        <div className='row'>
          <div className='col-sm-12 col-xl-8 justify-content-start d-flex'>
            <HeaderBreadcrumb>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                <BreadcrumbPortal />
                {!error ? (
                  <span style={{ whiteSpace: 'nowrap', fontSize: 14, opacity: 0.75 }}>
                    · {recuento}
                  </span>
                ) : null}
              </div>
            </HeaderBreadcrumb>
          </div>

          <div className='col-sm-12 col-xl-4 justify-content-end d-flex'>
            <div className='w-100 d-flex'>
              <div style={{ marginRight: '1rem' }}>
                <BusinessUnitMultiSelect
                  size='xl'
                  value={unidades}
                  onChange={setUnidades}
                  placeholder='Selecciona las unidades de negocio...'
                  compact
                />
              </div>

              <div className='search' style={{ minWidth: '120px' }}>
                <div className='searchbar__wrap'>
                  <h4 className='filter__title'>Buscador</h4>
                  <input
                    className='btn-filter search placeholder-text'
                    type='text'
                    value={texto}
                    placeholder='Buscar productos...'
                    onChange={(e) => setTexto(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div
            style={{
              margin: '10px 0',
              padding: '8px 12px',
              fontSize: 13.5,
              fontWeight: 600,
              color: '#a32218',
              background: '#f6e3e1',
              borderLeft: '3px solid #a32218',
            }}
          >
            {error}
          </div>
        ) : null}

        <div className='row'>
          <div className='col-12'>
            {cargando && items.length === 0 ? (
              <LoadingSpinner message='Cargando...' size='large' variant='primary' />
            ) : visibles.length > 0 ? (
              <div className='catalogue-grid'>
                {carpetas.map((item) => (
                  <FolderProduct
                    key={`cat-folder-${item.id_Articulo}`}
                    item={item}
                    pathname={`${base}/product/${item.id_Articulo}/${segmentoNombre(
                      item.nombre,
                    )}`}
                  />
                ))}
                {finales.map((item) => (
                  <ProductCard
                    key={`cat-final-${item.id_Articulo}`}
                    item={item}
                    pathname={`${base}/final/${item.id_Articulo}`}
                  />
                ))}
              </div>
            ) : (
              <div className='catalogue-no-results'>
                <div className='no-results-content'>
                  <div className='no-results-icon'>🔍</div>
                  <div className='no-results-text'>No hay productos disponibles</div>
                </div>
              </div>
            )}

            {cargando && items.length > 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Spinner />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    )
  }
}

export default CategoryBrowseV2
